<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  compact: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  modelValue: { type: Array, default: () => [] },
  options: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
const input = ref('');
const datalistId = 'post-sale-client-options';

const optionText = option =>
  [option.label, option.cpf_cnpj].filter(Boolean).join(' · ');
const optionFor = token =>
  props.options.find(option => String(option.id) === String(token));
const selected = computed(() =>
  props.modelValue.map(token => ({
    token,
    label: optionFor(token)
      ? optionText(optionFor(token))
      : String(token).replace(/^text:/, ''),
  }))
);

const commit = () => {
  const value = input.value.trim();
  if (!value) return;

  const option = props.options.find(item => optionText(item) === value);
  const token = option ? String(option.id) : `text:${value}`;
  if (!props.modelValue.includes(token)) {
    emit('update:modelValue', [...props.modelValue, token]);
  }
  input.value = '';
};

const remove = token =>
  emit(
    'update:modelValue',
    props.modelValue.filter(value => String(value) !== String(token))
  );
</script>

<template>
  <div class="grid gap-2">
    <label
      class="text-sm font-semibold text-lais-text-body"
      :class="props.compact ? 'sr-only' : ''"
      :for="datalistId"
    >
      {{ t('POST_SALE.CLIENT_FILTER.LABEL') }}
    </label>
    <div class="relative">
      <span
        class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#01988C]"
        :class="
          loading ? 'i-lucide-loader-circle animate-spin' : 'i-lucide-search'
        "
        aria-hidden="true"
      />
      <input
        :id="datalistId"
        v-model="input"
        :list="`${datalistId}-list`"
        type="search"
        autocomplete="off"
        :disabled="loading"
        :placeholder="t('POST_SALE.CLIENT_FILTER.PLACEHOLDER')"
        class="min-h-10 w-full rounded-lais border border-lais-border bg-white px-10 text-sm text-lais-text focus:border-[#01988C] focus:outline-none"
        @change="commit"
        @keyup.enter.prevent="commit"
      />
      <datalist :id="`${datalistId}-list`">
        <option
          v-for="option in options"
          :key="option.id"
          :value="optionText(option)"
        />
      </datalist>
    </div>
    <div v-if="selected.length" class="flex flex-wrap gap-2">
      <button
        v-for="item in selected"
        :key="item.token"
        type="button"
        class="inline-flex items-center gap-1 rounded-full border border-lais-border bg-white px-3 py-1 text-xs font-semibold text-lais-text-body hover:border-[#01988C]"
        :aria-label="t('POST_SALE.CLIENT_FILTER.REMOVE', { label: item.label })"
        @click="remove(item.token)"
      >
        <span>{{ item.label }}</span>
        <span class="i-lucide-x size-3" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>
